/**
 * Mobility MVP: Pelvic Tilt Camera Sheet
 * Full-height sheet for recording 10-second pelvic tilt video
 * Follows FIGMA #MVP-Camera-Sheet pattern
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { X } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { createCameraController } from '@/lib/cameraController';

interface PelvicTiltCameraSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onVideoReady: (blob: Blob) => void;
  /**
   * When true, render "sheet content" inline (no Radix Dialog/Sheet wrapper).
   * This avoids nesting a Sheet (Radix Dialog) inside another DialogContent,
   * which can cause overlay/z-index/focus issues and camera permission problems.
   */
  embedded?: boolean;
}

type CameraStatus = 'idle' | 'starting' | 'ready' | 'error';

export function PelvicTiltCameraSheet({ isOpen, onClose, onVideoReady, embedded = false }: PelvicTiltCameraSheetProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [timeLeft, setTimeLeft] = useState(10);
  const [hasRecording, setHasRecording] = useState(false);
  const [cameraStatus, setCameraStatus] = useState<CameraStatus>('idle');
  const [cameraError, setCameraError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const camRef = useRef(createCameraController());

  const startCamera = useCallback(async () => {
    if (!videoRef.current) return;
    setCameraError(null);
    setCameraStatus('starting');
    // #region agent log
    console.log('[PelvicTiltCameraSheet] startCamera clicked', { embedded, isOpen });
    // #endregion

    try {
      await camRef.current.start({
        videoEl: videoRef.current,
        facing: 'user', // Desktop/laptop compatibility (front camera)
        idealWidth: 1920,
        idealHeight: 1080,
      });
      setCameraStatus('ready');
      // #region agent log
      console.log('[PelvicTiltCameraSheet] camera ready');
      // #endregion
    } catch (e: any) {
      const msg =
        typeof e?.message === 'string'
          ? e.message
          : 'Unable to access camera. Check browser permissions and reload.';
      setCameraError(msg);
      setCameraStatus('error');
      // #region agent log
      console.error('[PelvicTiltCameraSheet] camera start failed', e);
      // #endregion
    }
  }, [embedded, isOpen]);

  // Cleanup camera on close/unmount
  useEffect(() => {
    if (!isOpen) return;
    return () => {
      camRef.current.stop();
    };
  }, [isOpen]);

  const startRecording = useCallback(async () => {
    const stream = camRef.current.getStream();
    if (!stream) return;

    try {
      recordedChunksRef.current = [];
      // Prefer a supported webm mime type; Safari may not support vp9.
      const candidates = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
      const chosen = candidates.find((t) => (window as any).MediaRecorder?.isTypeSupported?.(t));
      const mediaRecorder = chosen ? new MediaRecorder(stream, { mimeType: chosen }) : new MediaRecorder(stream);

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const firstType = recordedChunksRef.current[0]?.type;
        const recorderType = mediaRecorderRef.current?.mimeType;
        const blobType = firstType || recorderType || 'video/webm';
        const blob = new Blob(recordedChunksRef.current, { type: blobType });
        setHasRecording(true);
        onVideoReady(blob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setTimeLeft(10);

      // Countdown timer
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            stopRecording();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      console.error('Failed to start recording:', error);
    }
  }, [onVideoReady]);

  const stopRecording = useCallback(() => {
    // Stop based on MediaRecorder's actual state, not React state (which can be stale)
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, []);

  const handleClose = () => {
    stopRecording();
    camRef.current.stop();
    onClose();
  };

  const content = (
    <div className="relative h-full w-full bg-black flex flex-col">
      {/* Dismiss button */}
      <button
        onClick={handleClose}
        className="absolute top-4 left-4 z-50 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70"
      >
        <X className="h-5 w-5" />
      </button>

      {/* Video element */}
      <div className="flex-1 relative">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
        />

        {/* Camera enable overlay */}
        {cameraStatus !== 'ready' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 p-6">
            <div className="bg-black/70 text-white px-6 py-5 rounded-lg text-center max-w-sm w-full">
              <p className="text-lg font-semibold mb-2">Enable camera</p>
              <p className="text-sm text-gray-300 mb-4">
                We’ll use your camera to record a 10-second pelvic tilt video.
              </p>
              {cameraError && (
                <p className="text-sm text-red-300 mb-4 break-words">
                  {cameraError}
                </p>
              )}
              <Button
                onClick={startCamera}
                disabled={cameraStatus === 'starting'}
                className="w-full"
                type="button"
              >
                {cameraStatus === 'starting' ? 'Starting…' : 'Enable Camera'}
              </Button>
            </div>
          </div>
        )}

        {/* Instruction overlay */}
        {cameraStatus === 'ready' && !isRecording && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
            <div className="bg-black/70 text-white px-6 py-4 rounded-lg text-center max-w-sm">
              <p className="text-lg font-semibold mb-2">How to do the pelvic tilt</p>
              <div className="text-sm text-gray-200 space-y-2 text-left">
                <p>
                  - Keep your chest quiet and knees soft.
                </p>
                <p>
                  - <strong>Round/tuck</strong>: gently “tuck tailbone under” (belt buckle up).
                </p>
                <p>
                  - <strong>Arch</strong>: gently “stick tailbone back” (belt buckle down).
                </p>
                <p className="text-gray-300">
                  Aim for <strong>3–4 smooth reps</strong> during the 10 seconds.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Countdown overlay */}
        {isRecording && (
          <div className="absolute top-20 left-1/2 transform -translate-x-1/2">
            <div className="bg-black/70 text-white px-6 py-3 rounded-full">
              <div className="flex flex-col items-center">
                <span className="text-xs text-gray-300">Seconds remaining</span>
                <span className="text-2xl font-bold leading-tight">{timeLeft}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom controls */}
      <div className="absolute bottom-8 left-0 right-0 flex justify-center">
        {!isRecording && !hasRecording ? (
          <Button
            onClick={startRecording}
            disabled={cameraStatus !== 'ready'}
            size="lg"
            className="rounded-full h-16 w-16 bg-red-600 hover:bg-red-700 disabled:opacity-50"
            type="button"
          >
            <span className="text-white font-semibold">Record</span>
          </Button>
        ) : hasRecording ? (
          <div className="text-white text-center">
            <p className="mb-2">Recording complete!</p>
            <Button onClick={handleClose} variant="outline" className="bg-white text-black" type="button">
              Continue
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );

  // Embedded mode: no nested Sheet/Dialog wrapper
  if (embedded) {
    return <div className="h-full w-full">{content}</div>;
  }

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent side="bottom" className="h-screen p-0 max-h-screen">
        <SheetTitle className="sr-only">Pelvic Tilt Test</SheetTitle>
        <SheetDescription className="sr-only">Record a video of your pelvic tilt movement</SheetDescription>
        {content}
      </SheetContent>
    </Sheet>
  );
}
